import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAutoSave } from '@hooks/useAutoSave';

export interface CommittedTextFieldOptions {
  /** The saved value, as the server last reported it. */
  value: string;
  /** Rejects to put the field into its error state. */
  onSave: (next: string) => Promise<void>;
  /**
   * Whether a blank value is a real value. A label is not (the patient page
   * would show an unnamed item, and the API refuses it), so blanking one
   * restores the saved text; instructions are optional, so blanking them
   * clears them.
   */
  allowEmpty?: boolean;
  /**
   * Enter commits a single-line field. A multiline field keeps Enter for new
   * lines and commits on blur only.
   */
  multiline?: boolean;
}

export interface CommittedTextField {
  value: string;
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: Date | null;
  /** Typed and not yet committed or discarded. */
  isDirty: boolean;
  /** Worth showing a save indicator: editing, saving, or failed. */
  showStatus: boolean;
  /**
   * The last commit was refused and the screen still shows it. Cleared by the
   * next commit or by Escape — not read from `status`, which `useAutoSave`
   * leaves at 'error' until some later save succeeds.
   */
  failed: boolean;
  inputProps: {
    value: string;
    onChange: (event: { target: { value: string } }) => void;
    onFocus: () => void;
    onBlur: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  };
}

/**
 * A text field that writes ONLY when the admin commits: blur, or Enter on a
 * single-line field. Never while they type (GDN-3525).
 *
 * The field promises that Escape abandons an edit, and a background save can
 * break that promise: a keystroke debounce fires during any long pause, so an
 * edit the admin then abandoned had already been sent. Blur still commits, so
 * tabbing or clicking away loses nothing; only *pausing* is not a commit.
 * The ruling, and the guarantees this hook keeps, are recorded at
 * https://linear.app/jotpsych/issue/GDN-3525#comment-c8101cd8-9627-4f8e-ab39-a5fb5f6ef1ec
 *
 * Moved out of the catalog row when the row became an expandable table
 * (GDN-4142), so the label and the instructions share one set of rules rather
 * than two copies that could drift.
 */
export const useCommittedTextField = ({
  value: savedValue,
  onSave,
  allowEmpty = false,
  multiline = false,
}: CommittedTextFieldOptions): CommittedTextField => {
  const [value, setValue] = useState(savedValue);
  /** Text typed and not yet committed or discarded. */
  const draftRef = useRef<string | null>(null);
  /** Value of a save that has been issued and not yet confirmed. */
  const savingRef = useRef<string | null>(null);
  /**
   * What the field held when editing began: the target a discard restores,
   * and what a commit is compared against to decide it is a no-op.
   *
   * Deliberately not `savedValue`: while an earlier save is still in flight the
   * prop is the pre-save value, so comparing against it would treat focusing
   * and leaving without typing as a fresh edit and send that value again.
   */
  const startedWithRef = useRef(savedValue);
  /** The server's value as last reported, for a failed save to fall back to. */
  const serverValueRef = useRef(savedValue);
  serverValueRef.current = savedValue;
  /** The last commit the server refused, until the next commit replaces it. */
  const failedRef = useRef<string | null>(null);
  const [focused, setFocused] = useState(false);
  /** Re-render trigger: the refs above drive what the status shows. */
  const [, setRevision] = useState(0);
  const bump = () => setRevision(revision => revision + 1);

  // Adopt server-side changes (another admin, another tab) unless this field
  // has an uncommitted edit or an unconfirmed save that adopting would clobber.
  useEffect(() => {
    if (draftRef.current === null && savingRef.current === null) {
      setValue(savedValue);
      startedWithRef.current = savedValue;
    }
  }, [savedValue]);

  const save = useCallback(
    async (next: string) => {
      try {
        await onSave(next);
      } catch (error) {
        // The server never took `next`, so it is not a starting point: typing
        // it again and leaving must retry the save, not count as a no-op.
        if (savingRef.current === next) {
          failedRef.current = next;
          startedWithRef.current = serverValueRef.current;
        }
        bump();
        throw error;
      }
      // Clear the marker only if it still refers to the value that just saved.
      // An older request settling after a newer commit would otherwise mark the
      // newer text as confirmed, and the effect above would then adopt the
      // older server value over what is on screen. A rejection leaves the
      // marker set on purpose: screen and server disagree until resolved.
      if (savingRef.current === next) savingRef.current = null;
      bump();
    },
    [onSave],
  );

  /*
    `useAutoSave` is kept for what it does besides debouncing: it serialises
    writes on one tail, so a slow save is never overtaken by a later one, and
    it owns the status. `triggerAutoSave` is deliberately unused — see above.
  */
  const { saveImmediately, status, lastSavedAt } = useAutoSave<string>({ onSave: save });

  /** The single commit route. */
  const commit = () => {
    const typed = draftRef.current;
    draftRef.current = null;
    if (typed === null) return;

    const blank = typed.trim() === '';
    // Blank optional text is cleared, not stored as whitespace — the same as
    // the create dialog, which trims.
    const draft = blank && allowEmpty ? '' : typed;
    const startedWith = startedWithRef.current;
    if ((blank && !allowEmpty) || draft === startedWith) {
      setValue(startedWith);
      bump();
      return;
    }

    startedWithRef.current = draft;
    savingRef.current = draft;
    failedRef.current = null;
    setValue(draft);
    saveImmediately(draft);
    bump();
  };

  /** Escape abandons the edit: nothing was written, so nothing is undone. */
  const discard = () => {
    draftRef.current = null;
    // Cancelling after a refused save puts the field back to the server's
    // value, so nothing is unsaved any more: drop the refused value too, or the
    // row would keep warning about a change nobody is still trying to make.
    if (failedRef.current !== null) {
      failedRef.current = null;
      savingRef.current = null;
    }
    setValue(startedWithRef.current);
    bump();
  };

  return {
    value,
    status,
    lastSavedAt,
    isDirty: draftRef.current !== null || savingRef.current !== null,
    failed: failedRef.current !== null,
    showStatus:
      (focused && draftRef.current !== null) ||
      savingRef.current !== null ||
      failedRef.current !== null,
    inputProps: {
      value,
      onChange: event => {
        setValue(event.target.value);
        draftRef.current = event.target.value;
      },
      onFocus: () => {
        // A focus while a draft is pending (focus left for a menu and came back)
        // keeps the original starting point, so Escape still restores it.
        // After a refused save the screen shows text the server does not
        // have, so the starting point is the server's value, not the screen's.
        if (draftRef.current === null) {
          startedWithRef.current = failedRef.current !== null ? serverValueRef.current : value;
        }
        setFocused(true);
      },
      onBlur: () => {
        setFocused(false);
        commit();
      },
      onKeyDown: event => {
        if (event.key === 'Enter' && !multiline) {
          // Commit in place. The field stays focused, and the blur that comes
          // later finds no draft, so this can never save twice.
          event.preventDefault();
          commit();
          return;
        }
        if (event.key === 'Escape') {
          // Kept from bubbling: inside a dialog or an expanded row, Escape
          // means "undo my typing", not "close the thing I am typing in".
          event.preventDefault();
          event.stopPropagation();
          discard();
        }
      },
    },
  };
};
